import { useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { faArrowUpRightFromSquare, faChevronDown, faClock, faLocationDot } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Grid, Link, Stack, Typography } from "@mui/material"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { decodeFullPlusCode, isSearchablePlusCode } from "../../../../../../lib/plusCode"
import type { PlusCodeLocation } from "../../../../../../lib/plusCode"
import { ExternalLink } from "../../../../links/ExternalLink"
import type { SelectedPriceListItem } from "../../../createOrderTypes"

import { PriceListCard } from "./PriceListCard"
import type { MeetingServicePriceListsItem } from "./priceListsTypes"

const useStyles = makeStyles()((theme) => ({
    name: {
        fontWeight: 700,
    },
    meta: {
        color: theme.palette.tailwind.slate[500],
    },
    chevron: {
        fontSize: ".625rem",
        transition: "transform .15s ease",
    },
    chevronOpen: {
        transform: "rotate(180deg)",
    },
    map: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".875rem",
        overflow: "hidden",
    },
    mapFrame: {
        width: "100%",
        height: "13.75rem",
        border: 0,
        display: "block",
    },
}))

// The address is appended on purpose: a full plus code does not need it, but a shortened one
// ("9FWC+8X") cannot be resolved without the locality it belongs to. A malformed code is left
// out entirely - it would only pull the search away from the address.
const buildMapSearchUrl = (plusCode: string | undefined, address: string | undefined) => {
    const query = [plusCode && isSearchablePlusCode(plusCode) ? plusCode : undefined, address].filter(Boolean).join(" ")

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

// The decoded cell is ~14 m across, far too tight to read as a map, so the viewport is padded
// around its centre instead.
const MAP_LATITUDE_PADDING = 0.004
const MAP_LONGITUDE_PADDING = 0.008

const buildMapEmbedUrl = ({ latitude, longitude }: PlusCodeLocation) => {
    const bbox = [
        longitude - MAP_LONGITUDE_PADDING,
        latitude - MAP_LATITUDE_PADDING,
        longitude + MAP_LONGITUDE_PADDING,
        latitude + MAP_LATITUDE_PADDING,
    ].join(",")

    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude},${longitude}`
}

type MerchantPriceListsGroupProps = Readonly<{
    merchant: MeetingServicePriceListsItem
    searchRegex: RegExp | undefined
    selectedItem: SelectedPriceListItem | null
    onSelect: (item: SelectedPriceListItem) => void
    onToggleFavorite: (merchantId: number, priceListId: number) => void
}>

export function MerchantPriceListsGroup({
    merchant,
    searchRegex,
    selectedItem,
    onSelect,
    onToggleFavorite,
}: MerchantPriceListsGroupProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const [isMapOpen, setIsMapOpen] = useState(false)

    // only a full plus code can be turned into a position; a shortened one falls back to a link
    const plusCode = merchant.address?.plusCode
    const mapLocation = useMemo(() => (plusCode ? decodeFullPlusCode(plusCode) : null), [plusCode])

    const mapToggleLabel = l10n.getString(
        isMapOpen
            ? "create-order-select-price-list-address-hide-map"
            : "create-order-select-price-list-address-show-map",
    )

    return (
        <Stack gap="1rem">
            <Stack gap=".375rem">
                <Stack direction="row" gap=".75rem" alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" className={classes.name}>
                        {merchant.displayName}
                    </Typography>
                    {merchant.address?.displayName &&
                        (mapLocation ? (
                            <Link
                                component="button"
                                type="button"
                                color="inherit"
                                underline="hover"
                                className={classes.meta}
                                onClick={() => setIsMapOpen((prev) => !prev)}
                                aria-expanded={isMapOpen}
                                title={mapToggleLabel}
                                aria-label={mapToggleLabel}
                            >
                                <Stack direction="row" gap=".5rem" alignItems="center" flexWrap="nowrap">
                                    <FontAwesomeIcon icon={faLocationDot} size="xs" />
                                    <Typography variant="body2" component="span">
                                        {merchant.address.displayName}
                                    </Typography>
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        size="xs"
                                        className={cx(classes.chevron, isMapOpen && classes.chevronOpen)}
                                    />
                                </Stack>
                            </Link>
                        ) : merchant.address.plusCode ? (
                            <ExternalLink
                                href={buildMapSearchUrl(merchant.address.plusCode, merchant.address.displayName)}
                                color="inherit"
                                underline="hover"
                                className={classes.meta}
                                title={l10n.getString("create-order-select-price-list-address-map-link")}
                                aria-label={l10n.getString("create-order-select-price-list-address-map-link")}
                            >
                                <Stack direction="row" gap=".5rem" alignItems="center" flexWrap="nowrap">
                                    <FontAwesomeIcon icon={faLocationDot} size="xs" />
                                    <Typography variant="body2" component="span">
                                        {merchant.address.displayName}
                                    </Typography>
                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="xs" />
                                </Stack>
                            </ExternalLink>
                        ) : (
                            <Stack
                                direction="row"
                                gap=".5rem"
                                alignItems="center"
                                flexWrap="nowrap"
                                className={classes.meta}
                            >
                                <FontAwesomeIcon icon={faLocationDot} size="xs" />
                                <Typography variant="body2">{merchant.address.displayName}</Typography>
                            </Stack>
                        ))}
                </Stack>
                {/* the deadline is configured per shop, so it belongs to the group, not to each card */}
                {merchant.shop.deadline?.message && (
                    <Stack direction="row" gap=".5rem" alignItems="center" flexWrap="nowrap" className={classes.meta}>
                        <FontAwesomeIcon icon={faClock} size="xs" />
                        <Typography variant="body2">{merchant.shop.deadline.message}</Typography>
                    </Stack>
                )}
                {mapLocation && isMapOpen && (
                    <Box className={classes.map}>
                        <iframe
                            src={buildMapEmbedUrl(mapLocation)}
                            title={merchant.address?.displayName ?? merchant.displayName}
                            loading="lazy"
                            className={classes.mapFrame}
                        />
                    </Box>
                )}
            </Stack>
            <Grid container spacing="1rem">
                {merchant.priceLists.map((priceList) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`${merchant.id}-${priceList.id}`}>
                        <PriceListCard
                            merchant={merchant}
                            priceList={priceList}
                            searchRegex={searchRegex}
                            isSelected={
                                selectedItem?.merchantId === merchant.id && selectedItem?.priceListId === priceList.id
                            }
                            onSelect={onSelect}
                            onToggleFavorite={onToggleFavorite}
                        />
                    </Grid>
                ))}
            </Grid>
        </Stack>
    )
}
